import React, { useEffect } from 'react';
import PricingSection from '../../components/Home/PricingSection';

const RecruitmentLanding: React.FC = () => {
  // SEO per project policy (no external SEO libs)
  useEffect(() => {
    const title = 'AI Recruitment & ATS Platform | Explanatum';
    const desc = 'White-label careers portal, AI resume screening, pipeline automation, and interview scheduling—all without Greenhouse or Lever per-seat fees.';
    const url = 'https://explanatum.co.uk/modules/recruitment';
    document.title = title;

    // Basic meta update helper
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', desc);
    setOg('og:title', title);
    setOg('og:description', desc);
    setOg('og:url', url);

    // Canonical
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const w = window as any;
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  // FAQPage JSON-LD for rich results
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How does the white-label careers portal work?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Embed our job board on your website via iframe with your branding (colors, logo). Candidates apply directly, and applications flow into your ATS automatically—no job board fees required.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I use AI to screen resumes?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Yes. Upload CVs and our AI extracts skills, experience, education, and scores candidates against your job requirements in seconds—no manual screening needed.',
        },
      },
      {
        '@type': 'Question',
        name: 'How much can I save vs traditional ATS?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Traditional ATS like Greenhouse or Lever cost £5,000-15,000+ per year. Explanatum includes unlimited jobs, candidates, and users for one fixed price—saving 70-90% annually.',
        },
      },
      {
        '@type': 'Question',
        name: 'Do I need an account to receive applications?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Yes, you need an Explanatum account to manage jobs and applications. Candidates can apply without creating accounts.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I customize the application form?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Yes, you can add custom screening questions per job and collect resumes, cover letters, LinkedIn profiles, and portfolio links.',
        },
      },
    ],
  } as const;

  return (
    <main className="min-h-screen bg-gray-50" role="main">
      {/* FAQPage Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white text-gray-600" role="region" aria-label="Hero">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-1 text-sm font-semibold text-blue-700">
                <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                AI Recruitment & ATS Platform
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Hire faster without per-seat ATS fees.
                <span className="mt-2 block text-gray-900">
                  White-label portal included.
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-xl">
                Embed a branded careers page, automate AI resume screening, manage pipelines, schedule interviews, and generate offers—without Greenhouse, Lever, or Indeed job board fees.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#free-trial"
                  onClick={() => track('click_hero_cta', { page: 'recruitment_landing', placement: 'hero' })}
                  className="inline-flex items-center justify-center rounded-full bg-blue-600 hover:bg-blue-700 transition-colors px-6 py-3 text-base font-semibold text-white shadow-lg transition hover:scale-[1.02]"
                >
                  Start 7‑day trial
                </a>
                <a
                  href="#compare"
                  className="inline-flex items-center justify-center rounded-full border border-blue-600/50 px-6 py-3 text-base font-semibold text-gray-600 hover:bg-blue-50 transition-colors"
                >
                  Compare to Greenhouse & Lever
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  £5k-15k saved annually vs traditional ATS
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-sky-400" />
                  AI resume screening & scoring
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  Embeddable white-label job board
                </span>
              </div>
            </div>
            <aside className="relative" aria-label="Features">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-blue-50 blur-3xl" aria-hidden="true" />
              <div className="absolute -bottom-10 -right-8 h-36 w-36 rounded-full bg-cyan-50 blur-3xl" aria-hidden="true" />
              <nav className="relative rounded-3xl border border-gray-200 bg-white shadow-2xl " aria-labelledby="recruitment-features-title">
                <div className="px-6 py-5 border-b border-gray-200">
                  <h2 id="recruitment-features-title" className="text-lg font-semibold text-gray-900">Complete recruitment suite</h2>
                  <p className="text-sm text-gray-500 mt-1">Everything you need from job posting to offer acceptance.</p>
                </div>
                <ol className="p-6 space-y-2 list-decimal list-inside text-gray-600">
                  <li>White-label careers portal (embeddable)</li>
                  <li>Job posting & management</li>
                  <li>AI-powered resume screening</li>
                  <li>Candidate pipeline & Kanban board</li>
                  <li>Interview scheduling & feedback</li>
                  <li>Offer letter generation</li>
                  <li>Email template automation</li>
                  <li>Analytics & reporting</li>
                  <li>Custom screening questions</li>
                  <li>Referral tracking</li>
                  <li>Talent pool management</li>
                </ol>
              </nav>
            </aside>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 py-12" role="region" aria-label="Features">
        <h2 className="text-2xl font-semibold text-gray-900">What you get</h2>
        <div className="mt-6 grid md:grid-cols-3 gap-6">
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="font-medium text-gray-900">White-Label Portal</h3>
            <p className="text-gray-700 mt-2">Embed a branded careers page on your website. Candidates apply directly—no job board fees.</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="font-medium text-gray-900">AI Resume Screening</h3>
            <p className="text-gray-700 mt-2">Upload CVs, extract data, score candidates, and rank by fit automatically.</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="font-medium text-gray-900">Pipeline Management</h3>
            <p className="text-gray-700 mt-2">Drag-and-drop Kanban boards, custom stages, and automated email sequences.</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="font-medium text-gray-900">Interview Scheduling</h3>
            <p className="text-gray-700 mt-2">Schedule interviews, collect feedback, and score candidates with custom scorecards.</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="font-medium text-gray-900">Offer Generation</h3>
            <p className="text-gray-700 mt-2">Generate offer letters, track responses, and automate acceptance workflows.</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="font-medium text-gray-900">Analytics & Reporting</h3>
            <p className="text-gray-700 mt-2">Track time-to-hire, source effectiveness, conversion rates, and pipeline health.</p>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" aria-label="Comparison" className="relative overflow-hidden bg-white py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">Why startups choose Explanatum over enterprise ATS</h2>
            <p className="mt-4 text-base text-gray-500">Stop paying per-seat fees for basic recruiting features. Get unlimited users, jobs, and candidates for one fixed price.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[{
              title: 'Explanatum Recruitment',
              points: [
                'Unlimited jobs, candidates, and users included',
                'White-label careers portal with iframe embedding',
                'AI resume screening with automatic skill extraction',
                'Complete ATS with pipeline, interviews, and offers',
                'Integrates with CRM, staff management, and AI consultant',
                'One-click offer generation and email automation',
                '£0 per-seat fees—fixed monthly price'
              ],
              accent: 'border-blue-300 bg-blue-50'
            }, {
              title: 'Greenhouse & Lever',
              points: [
                'Per-seat pricing: £5,000-15,000+ per year',
                'Basic job posting—no embeddable white-label portal',
                'Manual resume review or expensive integrations',
                'Enterprise features locked behind higher tiers',
                'Separate subscriptions for analytics and automation',
                'Custom workflows require developer setup',
                'Additional costs for API access and support'
              ],
              accent: 'border-gray-200 bg-gray-50'
            }, {
              title: 'Indeed, LinkedIn, & Job Boards',
              points: [
                '£300-2,000 per job posting',
                'No ATS—need separate tool for pipeline management',
                'No resume parsing—manual data entry required',
                'Limited to job advertising only',
                'Candidates scattered across multiple platforms',
                'No interview scheduling or offer management',
                '£5,000-15,000+ annually for 10 positions'
              ],
              accent: 'border-gray-200 bg-gray-50'
            }].map((column) => (
              <article key={column.title} className={`rounded-3xl border ${column.accent} p-6 shadow-xl shadow-lg`}>
                <h3 className="text-lg font-semibold text-gray-900">{column.title}</h3>
                <ul className="mt-5 space-y-3 text-sm text-gray-900">
                  {column.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-gray-900">
                      <span className="mt-1 inline-flex h-1.5 w-1.5 rounded-full bg-blue-600" />
                      <span className="text-gray-900">{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <div className="mt-14 rounded-3xl border border-indigo-400/30 bg-indigo-400/10 p-8 text-center ">
            <h3 className="text-2xl font-semibold text-gray-900">"Can I post jobs on my website without paying Indeed or LinkedIn?"</h3>
            <p className="mt-3 text-base text-gray-600">Yes! Embed our white-label careers portal, collect applications directly, and manage your entire hiring pipeline—all included.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <a
                href="#free-trial"
                onClick={() => track('click_compare_cta', { page: 'recruitment_landing' })}
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-indigo-500 via-blue-600 to-sky-500 px-6 py-3 text-base font-semibold text-gray-900 shadow-lg shadow-indigo-500/30 hover:scale-[1.02]"
              >
                Start hiring now
              </a>
              <a
                href="/recruitment"
                className="inline-flex items-center justify-center rounded-full border border-blue-600/60 px-6 py-3 text-base font-semibold text-gray-600 hover:border-slate-300/70"
              >
                View demo
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <PricingSection />
    </main>
  );
};

export default RecruitmentLanding;
